import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { useHistory, useLocation } from 'react-router-dom'
import { useMutation } from '@apollo/react-hooks'
import { yupResolver } from '@hookform/resolvers'
import {
  Col,
  Collapse,
  Form,
  Modal,
  Pagination,
  Row,
  Table,
  Typography,
} from 'antd'
import {
  differenceBy,
  drop,
  find,
  flatMap,
  has,
  includes,
  mergeWith,
  range,
  size,
  slice,
} from 'lodash-es'
import PropTypes from 'prop-types'

import {
  Breadcrumb,
  Checkbox,
  FormDate,
  FormInternalLink,
  FormText,
  HiddenCheckboxes,
  HiddenInputs,
  Input,
} from '../../../core/components'
import { Button } from '../../../core/components/styled'
import { createGoogleCoordinates } from '../../../core/utils/services'
import {
  createUrl,
  getQueryParams,
} from '../../../core/utils/services/queryParams'

import {
  GooglePlaceLink,
  HistoryDrawer,
  OpenLink,
  Spinner,
  VisibilityContainer,
} from '../../../common/components'
import {
  PageActions,
  PageHeader,
  PageTitle,
} from '../../../common/components/styled'
import {
  BRAND_DEFINITION_TASK,
  BRAND_DEFINITION_TASKS_PERMISSIONS,
  DEFAULT_PAGE_SIZE,
} from '../../../common/constants'
import { PAGE_SIZE_OPTIONS } from '../../../common/constants/pagination'
import {
  showErrorNotification,
  showSuccessNotification,
} from '../../../common/helpers'
import TaskResolveMessage from '../common/TaskResolveMessage'

import { AuthContext } from '../../../account/contexts'
import { BUSINESS_STATUSES } from '../../../brands/constants'
import {
  BRAND_DEFINITION,
  DONE,
  MANUAL_CHANGE,
  QC,
  QQA,
  REOPENED,
  TASK_AWAITING_REASONS,
  TASK_REOPEN_REASONS,
  UPDATE,
} from '../../constants'
import {
  ASSIGN_BRAND_DEFINITION_TASK,
  MARK_BRAND_DEFINITION_TASK_AS_AWAITING,
  MARK_BRAND_DEFINITION_TASK_MISC_AS_QC_VERIFIED,
  MARK_BRAND_DEFINITION_TASK_MISC_AS_VERIFIED,
  REOPEN_BRAND_DEFINITION_TASK,
  UNASSIGN_BRAND_DEFINITION_TASK,
  UNMARK_BRAND_DEFINITION_TASK_AS_AWAITING,
} from '../../graphql/mutations'
import {
  getBreadcrumbItemsForBrandDefinitionTaskPage,
  getTaskActionButtonColors,
  hasCoordinates,
} from '../../helpers'
import { mergeFormValues } from '../../utils'
import { BrandDefinitionMap } from '../maps'
import {
  AwaitingReasonField,
  MarkMiscTaskAsVerifiedModal,
  MiscTaskVerificationThreeStateSwitch,
  TaskActionSection,
  TaskViewsStatus,
} from '../pages/sections'

import { brandDefinitionTaskState } from './global-states'
import { brandDefinitionTaskSchema } from './schemas'
import TaskViewBanner from './task-view-banner'
import { brandDefinitionTaskStateValidator } from './validators'

const getFormState = (taskState, page, pageSize = DEFAULT_PAGE_SIZE) => ({
  ...taskState,
  locations: slice(
    taskState.locations,
    (page - 1) * pageSize,
    (page - 1) * pageSize + pageSize,
  ),
})

const BrandDefinitionTaskForm = ({
  task,
  isSavingTask,
  isResolvingTask,
  isRefetchingTask,
  onSubmit,
}) => {
  const currentView = useMemo(
    () => find(task.views, view => task.currentViewType === view.type),
    [task.views, task.currentViewType],
  )

  const shouldHighlightField = fieldName =>
    currentView.status === UPDATE &&
    includes(currentView.modifiedBrandFields, fieldName)

  const { user: loggedInUser } = useContext(AuthContext)
  const [currentPage, setCurrentPage] = useState(1)

  const history = useHistory()
  const { pathname, search } = useLocation()
  const { pageSize: currentPageSize = DEFAULT_PAGE_SIZE } = getQueryParams(
    search,
  )

  const setCurrentPageSize = useCallback(
    pageSize => history.push(createUrl(pathname, search, { pageSize })),
    [history, search, pathname],
  )

  const taskStateCurrentValues = useMemo(
    () => brandDefinitionTaskState(task, currentView),
    [task, currentView],
  )

  const [cachedBrandWebsiteValue, setCachedBrandWebsiteValue] = useState(
    task.brand.website,
  )

  const [taskState, setTaskState] = useState(taskStateCurrentValues)

  const [disabledLocationsForMap, setDisabledLocationsForMap] = useState(
    drop(taskState.locations, DEFAULT_PAGE_SIZE),
  )

  const mapRef = useRef(null)

  const [isQCVerified, setIsQCVerified] = useState(false)

  const shouldShowReopenButton = useMemo(
    () => currentView.type === QQA && currentView.status === DONE,
    [currentView.type, currentView.status],
  )

  const [
    isMarkMiscTaskAsVerifiedModalOpen,
    setIsMarkMiscTaskAsVerifiedModalOpen,
  ] = useState(false)

  const formMethods = useForm({
    defaultValues: getFormState(taskState, currentPage, currentPageSize),
    resolver: yupResolver(brandDefinitionTaskSchema),
  })

  const {
    handleSubmit,
    getValues,
    setValue,
    watch,
    reset,
    control,
    errors,
    formState: { isDirty, isSubmitted },
  } = formMethods

  useEffect(() => {
    const formValues = getFormState(
      taskStateCurrentValues,
      1,
      DEFAULT_PAGE_SIZE,
    )
    setTaskState(taskStateCurrentValues)

    setDisabledLocationsForMap(
      drop(taskStateCurrentValues.locations, DEFAULT_PAGE_SIZE),
    )

    reset(formValues)
  }, [taskStateCurrentValues, reset])

  const { fields: locations } = useFieldArray({
    control,
    name: 'locations',
  })

  const [assignTask, { loading: isAssigningTask }] = useMutation(
    ASSIGN_BRAND_DEFINITION_TASK,
    {
      onCompleted({ assignBrandDefinitionTask }) {
        const { task } = getValues()

        const updatedTask = {
          ...task,
          modifiedDate: assignBrandDefinitionTask.modifiedDate,
          isAssigned: true,
          assignedTo: {
            id: loggedInUser.id,
            name: loggedInUser.name,
          },
        }

        setValue('task', updatedTask)

        showSuccessNotification({
          message: 'Brand definition task assigned.',
        })
      },
      onError(error) {
        showErrorNotification({
          message: 'Brand definition assign failed.',
          error,
        })
      },
    },
  )

  const [unassignTask, { loading: isUnassigningTask }] = useMutation(
    UNASSIGN_BRAND_DEFINITION_TASK,
    {
      onCompleted({ unassignBrandDefinitionTask }) {
        const { task } = getValues()

        const updatedTask = {
          ...task,
          modifiedDate: unassignBrandDefinitionTask.modifiedDate,
          isAssigned: false,
          assignedTo: {
            id: '',
            name: '',
          },
        }

        setValue('task', updatedTask)

        showSuccessNotification({
          message: 'Brand definition task unassigned.',
        })
      },
      onError({ message }) {
        showErrorNotification({
          message: 'Brand definition task unassign failed.',
          description: message,
        })
      },
    },
  )

  const [
    markTaskAsAwaiting,
    { loading: isMarkingTaskAsAwaiting },
  ] = useMutation(MARK_BRAND_DEFINITION_TASK_AS_AWAITING, {
    onCompleted({ markBrandDefinitionTaskAsAwaiting }) {
      const { task } = getValues()

      const updatedTask = {
        ...task,
        modifiedDate: markBrandDefinitionTaskAsAwaiting.modifiedDate,
        isAwaiting: true,
        awaitingReason:
          TASK_AWAITING_REASONS[
            markBrandDefinitionTaskAsAwaiting.awaitingReason
          ].name,
        awaitingReasonComment:
          markBrandDefinitionTaskAsAwaiting.awaitingReasonComment,
      }

      setValue('task', updatedTask)

      showSuccessNotification({
        message: 'Brand definition task marked as awaiting.',
      })
    },
    onError({ message }) {
      showErrorNotification({
        message: 'Brand definition task mark as awaiting failed.',
        description: message,
      })
    },
  })

  const [
    unmarkTaskAsAwaiting,
    { loading: isUnmarkingTaskAsAwaiting },
  ] = useMutation(UNMARK_BRAND_DEFINITION_TASK_AS_AWAITING, {
    onCompleted({ unmarkBrandDefinitionTaskAsAwaiting }) {
      const { task } = getValues()

      const updatedTask = {
        ...task,
        modifiedDate: unmarkBrandDefinitionTaskAsAwaiting.modifiedDate,
        isAwaiting: false,
        awaitingReason: '',
        awaitingReasonComment: '',
      }

      setValue('task', updatedTask)

      showSuccessNotification({
        message: 'Brand definition task unmarked as awaiting.',
      })
    },
    onError({ message }) {
      showErrorNotification({
        message: 'Brand definition task unmark as awaiting failed.',
        description: message,
      })
    },
  })

  const [reopenTask, { loading: isReopeningTask }] = useMutation(
    REOPEN_BRAND_DEFINITION_TASK,
    {
      onCompleted({
        reopenBrandDefinitionTask: {
          modifiedDate,
          reopenedDate,
          assignedTo,
          currentViewType,
          views,
        },
      }) {
        const { task } = getValues()

        const updatedTask = {
          ...task,
          isAssigned: !!assignedTo,
          assignedTo: {
            id: assignedTo?.id,
            name: assignedTo?.name,
          },
          modifiedDate,
          reopenedBy: {
            id: loggedInUser.id,
            name: loggedInUser.name,
          },
          reopenedDate,
          reopenReason: TASK_REOPEN_REASONS[MANUAL_CHANGE].name,
          status: REOPENED,
          currentViewType,
          views,
        }

        setValue('task', updatedTask)

        showSuccessNotification({
          message: 'Brand definition task reopened.',
        })
      },
      onError(error) {
        showErrorNotification({
          message: 'Brand definition task reopen failed.',
          error,
        })
      },
    },
  )

  const [
    markMiscTaskAsVerified,
    { loading: isMarkingMiscTaskAsVerified },
  ] = useMutation(MARK_BRAND_DEFINITION_TASK_MISC_AS_VERIFIED, {
    onCompleted({
      markBrandDefinitionTaskMiscAsVerified: {
        miscTaskVerifiedDate,
        miscTaskVerifiedComment,
      },
    }) {
      const { task } = getValues()

      const updatedTask = {
        ...task,
        miscTaskVerifiedBy: {
          id: loggedInUser.id,
          name: loggedInUser.name,
        },
        miscTaskVerifiedDate,
        miscTaskVerifiedComment,
      }

      setValue('task', updatedTask)

      showSuccessNotification({
        message: 'Miscellaneous task marked as verified.',
      })
    },
    onError({ message }) {
      showErrorNotification({
        message: 'Miscellaneous task mark as verified failed.',
        description: message,
      })
    },
  })

  const [
    markMiscTaskAsQCVerified,
    { loading: isMarkingMiscTaskAsQCVerified },
  ] = useMutation(MARK_BRAND_DEFINITION_TASK_MISC_AS_QC_VERIFIED, {
    onCompleted({
      markBrandDefinitionTaskMiscAsQCVerified: {
        miscTaskQCVerifiedDate,
        miscTaskQCVerifiedComment,
      },
    }) {
      const { task } = getValues()

      const updatedTask = {
        ...task,
        miscTaskQCVerifiedBy: {
          id: loggedInUser.id,
          name: loggedInUser.name,
        },
        miscTaskQCVerifiedDate,
        miscTaskQCVerifiedComment,
      }

      setValue('task', updatedTask)

      showSuccessNotification({
        message: 'Miscellaneous task marked as QC verified.',
      })
    },
    onError({ message }) {
      showErrorNotification({
        message: 'Miscellaneous task mark as QC verified failed.',
        description: message,
      })
    },
  })

  const onAssignTask = useCallback(
    () =>
      assignTask({
        variables: { id: task.id, toUserId: loggedInUser.id },
      }),
    [assignTask, task, loggedInUser],
  )

  const onUnassignTask = useCallback(
    () => unassignTask({ variables: { id: task.id } }),
    [unassignTask, task],
  )

  const onSave = useCallback(async () => {
    setValue('task.isDone', false)
    setCurrentPage(1)
    handleSubmit(onSubmit)()
  }, [setValue, handleSubmit, onSubmit])

  const onDone = useCallback(async () => {
    setValue('task.isDone', true)
    handleSubmit(async formValues => {
      const updatedTaskState = mergeWith(taskState, formValues, mergeFormValues)
      const isValid = await brandDefinitionTaskStateValidator(updatedTaskState)

      if (isValid) {
        onSubmit(updatedTaskState)
      }
    })()
  }, [setValue, taskState, handleSubmit, onSubmit])

  const onMarkTaskAsAwaiting = useCallback(
    awaitingReasonData =>
      markTaskAsAwaiting({
        variables: { id: task.id, ...awaitingReasonData },
      }),
    [markTaskAsAwaiting, task],
  )

  const onUnmarkTaskAsAwaiting = useCallback(() => {
    unmarkTaskAsAwaiting({ variables: { id: task.id } })
  }, [unmarkTaskAsAwaiting, task])

  const onReopenTask = useCallback(
    () => reopenTask({ variables: { id: task.id } }),
    [reopenTask, task],
  )

  const onVerifiedButtonClick = () => {
    setIsQCVerified(false)
    setIsMarkMiscTaskAsVerifiedModalOpen(true)
  }

  const onQCVerifiedButtonClick = () => {
    setIsQCVerified(true)
    setIsMarkMiscTaskAsVerifiedModalOpen(true)
  }

  const handleMarkMiscTaskAsVerified = useCallback(
    ({ comment }) => {
      const data = { variables: { id: task.id, comment } }
      setIsMarkMiscTaskAsVerifiedModalOpen(false)

      return isQCVerified
        ? markMiscTaskAsQCVerified(data)
        : markMiscTaskAsVerified(data)
    },
    [isQCVerified, markMiscTaskAsVerified, markMiscTaskAsQCVerified, task],
  )

  const onMapInitialized = useCallback(map => {
    mapRef.current = map
  }, [])

  const centerMapToLocation = useCallback(coordinates => {
    const googleCoordinates = createGoogleCoordinates(
      coordinates.latitude,
      coordinates.longitude,
    )

    mapRef.current.map.panTo(googleCoordinates)
    mapRef.current.map.setZoom(16)
  }, [])

  const getLocationRowClassName = record => {
    if (
      includes([QC, QQA], currentView.type) &&
      includes(
        currentView.modifiedLocationIdsUnreviewedByDefiner,
        Number(record.id),
      )
    ) {
      return 'restoredRow'
    }

    if (
      currentView.status === UPDATE &&
      includes(currentView.modifiedLocationIds, Number(record.id))
    ) {
      return 'requiresReviewRow'
    }

    return undefined
  }

  const tableColumns = [
    {
      title: '#',
      dataIndex: 'position',
      width: '75px',
      render: (position, location, index) => (
        <FormText name={`locations.${index}.position`} />
      ),
    },
    {
      title: 'ID',
      dataIndex: 'id',
      width: '80px',
      render: (id, location, index) => (
        <FormInternalLink
          name={`locations.${index}.id`}
          transform={id => `/brands/locations/${id}`}
        />
      ),
    },
    {
      title: 'Location',
      dataIndex: 'name',
      width: '200px',
      render: (name, location, index) =>
        watch('brand.useBrandNameAsLocationName') ? (
          watch('brand.name')
        ) : (
          <FormText name={`locations.${index}.name`} />
        ),
    },
    {
      title: 'Google place',
      width: '300px',
      render: (row, location, index) => {
        const coordinates = watch(`locations.${index}.coordinates`)

        return (
          <>
            <GooglePlaceLink
              googlePlaceId={watch(`locations.${index}.googlePlaceId`)}
              googleMapsUri={watch(`locations.${index}.googleMapsUri`)}
            />
            {hasCoordinates(coordinates) && (
              <Button
                className="u-noPadding"
                margin="no no no small"
                type="link"
                onClick={() => centerMapToLocation(coordinates)}
              >
                Map
              </Button>
            )}
          </>
        )
      },
    },
    {
      title: 'Website',
      dataIndex: 'website',
      width: '250px',
      ellipsis: true,
      render: website => (
        <Typography.Text>
          <a href={website} target="_blank" rel="noopener noreferrer">
            {website}
          </a>
        </Typography.Text>
      ),
    },
    {
      title: 'Phone Number',
      dataIndex: 'phone',
      width: '150px',
      render: phone => phone,
    },
    {
      title: 'Business status',
      dataIndex: 'businessStatus',
      width: '150px',
      render: businessStatus => BUSINESS_STATUSES[businessStatus]?.name,
    },
    {
      render: (row, location, index) => (
        <HiddenInputs
          names={[
            `locations.${index}.id`,
            `locations.${index}.status`,
            `locations.${index}.website`,
            `locations.${index}.phone`,
            `locations.${index}.businessStatus`,
            `locations.${index}.exclusionReason`,
            `locations.${index}.googlePlaceId`,
            `locations.${index}.googleMapsUri`,
            `locations.${index}.coordinates.latitude`,
            `locations.${index}.coordinates.longitude`,
          ]}
        />
      ),
    },
  ]

  const onPageChange = useCallback(
    (page, pageSize) => {
      const formValues = getValues()
      if (isDirty && !isSubmitted) {
        Modal.confirm({
          title: 'Unsaved changes',
          content:
            'There are changes on the page that are not saved. Do you want to continue?',
          onOk() {
            const updatedTaskState = mergeWith(
              taskState,
              formValues,
              mergeFormValues,
            )

            setTaskState(updatedTaskState)
            const formState = getFormState(updatedTaskState, page, pageSize)

            const nextPageDisabledLocations = differenceBy(
              taskState.locations,
              formState.locations,
              'id',
            )

            setDisabledLocationsForMap(nextPageDisabledLocations)
            reset(formState)
            setCurrentPage(page)
            setCurrentPageSize(pageSize)
          },
        })
      } else {
        const updatedTaskState = mergeWith(
          taskState,
          formValues,
          mergeFormValues,
        )

        setTaskState(updatedTaskState)
        const formState = getFormState(updatedTaskState, page, pageSize)

        const nextPageDisabledLocations = differenceBy(
          taskState.locations,
          formState.locations,
          'id',
        )

        setDisabledLocationsForMap(nextPageDisabledLocations)
        reset(formState)
        setCurrentPage(page)
        setCurrentPageSize(pageSize)
      }
    },
    [getValues, taskState, reset, isDirty, isSubmitted, setCurrentPageSize],
  )

  const taskStatus = watch('task.status')
  const miscTaskVerifiedComment = watch('task.miscTaskVerifiedComment')
  const miscTaskQCVerifiedComment = watch('task.miscTaskQCVerifiedComment')
  const currentViewType = watch('task.currentViewType')

  const breadcrumbItems = useMemo(
    () => getBreadcrumbItemsForBrandDefinitionTaskPage(task),
    [task],
  )

  return (
    <>
      <TaskViewBanner currentViewType={currentViewType} />
      {(isAssigningTask ||
        isUnassigningTask ||
        isSavingTask ||
        isResolvingTask ||
        isMarkingTaskAsAwaiting ||
        isUnmarkingTaskAsAwaiting ||
        isReopeningTask ||
        isRefetchingTask) && <Spinner size="large" />}

      <Breadcrumb currentTaskType={BRAND_DEFINITION} items={breadcrumbItems} />

      <PageHeader sticky style={{ top: 40 }}>
        <PageTitle>
          Brand definition task
          <HistoryDrawer
            entityType={BRAND_DEFINITION_TASK}
            entityId={task.id}
          />
          <TaskViewsStatus task={watch('task')} />
        </PageTitle>
        <PageActions>
          <TaskActionSection
            task={watch('task')}
            taskType={BRAND_DEFINITION}
            permissions={BRAND_DEFINITION_TASKS_PERMISSIONS}
            onAssignTask={onAssignTask}
            onUnassignTask={onUnassignTask}
            onSave={onSave}
            onDone={onDone}
            onMarkTaskAsAwaiting={onMarkTaskAsAwaiting}
            onUnmarkTaskAsAwaiting={onUnmarkTaskAsAwaiting}
            onReopenTask={shouldShowReopenButton && onReopenTask}
            cancelUrl="/tasks/brand-definition"
            doneButtonTitle={
              <TaskResolveMessage currentViewType={currentViewType} />
            }
            buttonColors={getTaskActionButtonColors(currentViewType)}
          />
        </PageActions>
      </PageHeader>

      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Collapse defaultActiveKey={['brand-section', 'task-section']}>
            <Collapse.Panel
              header="Brand"
              key="brand-section"
              style={has(errors, 'brand') ? { border: '1px solid red' } : {}}
            >
              <Row gutter={16}>
                <Col span={2}>
                  <FormInternalLink
                    name="brand.id"
                    label="ID"
                    transform={id => `/brands/${id}`}
                  />
                </Col>
                <Col span={6}>
                  <Input
                    name="brand.name"
                    label="Name"
                    highlighted={shouldHighlightField('name')}
                  />
                </Col>
                <Col span={8}>
                  <Input
                    name="brand.website"
                    label="Website"
                    disabled={watch('brand.isHasNoWebsiteConfirmed')}
                    highlighted={shouldHighlightField('website')}
                    addonAfter={
                      <OpenLink
                        onClick={() =>
                          !watch('brand.isHasNoWebsiteConfirmed') &&
                          window.open(getValues('brand.website'))
                        }
                      />
                    }
                  />
                </Col>
                <Col span={2}>
                  <Checkbox
                    name="brand.isHasNoWebsiteConfirmed"
                    label="No website"
                    highlighted={shouldHighlightField(
                      'isHasNoWebsiteConfirmed',
                    )}
                    onCheckboxChange={isHasNoWebsiteConfirmed => {
                      if (isHasNoWebsiteConfirmed) {
                        setCachedBrandWebsiteValue(getValues('brand.website'))
                        setValue('brand.website', null)
                      }
                      if (!isHasNoWebsiteConfirmed) {
                        setValue('brand.website', cachedBrandWebsiteValue)
                      }
                    }}
                  />
                </Col>
                <Col span={4}>
                  <Checkbox
                    name="brand.useBrandNameAsLocationName"
                    highlighted={shouldHighlightField(
                      'useBrandNameAsLocationName',
                    )}
                    label="Use brand name as location name"
                  />
                </Col>
                <Col span={2}>
                  <FormText
                    name="brand.businessStatus"
                    label="Business status"
                  />
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={3}>
                  <FormDate name="brand.modifiedDate" label="Updated at" />
                </Col>
              </Row>
            </Collapse.Panel>
            <Collapse.Panel header="Task" key="task-section">
              <Row gutter={16}>
                <Col span={3}>
                  <AwaitingReasonField
                    valueFieldName="task.awaitingReason"
                    titleFieldName="task.awaitingReasonComment"
                    label="Awaiting reason"
                  />
                </Col>
                <Col span={3}>
                  <FormDate label="Updated at" name="task.modifiedDate" />
                </Col>
                <VisibilityContainer isHidden={taskStatus !== REOPENED}>
                  <>
                    <Col span={3}>
                      <FormText
                        label="Reopened by"
                        name="task.reopenedBy.name"
                      />
                    </Col>
                    <Col span={3}>
                      <FormDate label="Reopened at" name="task.reopenedDate" />
                    </Col>
                    <Col span={5}>
                      <FormText
                        label="Reopen reason"
                        name="task.reopenReason"
                      />
                    </Col>
                  </>
                </VisibilityContainer>
                <Col span={3}>
                  <Form.Item label="Miscellaneous task">
                    <MiscTaskVerificationThreeStateSwitch
                      task={watch('task')}
                      onVerifiedButtonClick={onVerifiedButtonClick}
                      onQCVerifiedButtonClick={onQCVerifiedButtonClick}
                      loading={
                        isMarkingMiscTaskAsVerified ||
                        isMarkingMiscTaskAsQCVerified
                      }
                      loggedInUser={loggedInUser}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Collapse.Panel>
          </Collapse>
          <BrandDefinitionMap
            locations={locations}
            disabledLocations={disabledLocationsForMap}
            onMapInitialized={onMapInitialized}
          />
          <Table
            rowKey="id"
            scroll={{ x: 1600 }}
            columns={tableColumns}
            dataSource={locations}
            pagination={false}
            rowClassName={getLocationRowClassName}
          />
          <Row type="flex" justify="end">
            <Pagination
              showSizeChanger
              current={currentPage}
              pageSize={currentPageSize}
              style={{ marginTop: '15px' }}
              defaultPageSize={DEFAULT_PAGE_SIZE}
              total={size(taskState.locations)}
              onChange={onPageChange}
              onShowSizeChange={onPageChange}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
            />
          </Row>
          <HiddenInputs
            names={[
              'task.id',
              'task.assignedTo.id',
              'task.assignedTo.name',
              'task.status',
              'task.awaitingReason',
              'task.awaitingReasonComment',
              'task.reopenedDate',
              'task.reopenedBy.id',
              'task.reopenedBy.name',
              'task.reopenReason',
              'task.miscTaskVerifiedBy.id',
              'task.miscTaskVerifiedBy.name',
              'task.miscTaskVerifiedDate',
              'task.miscTaskVerifiedComment',
              'task.miscTaskQCVerifiedBy.id',
              'task.miscTaskQCVerifiedBy.name',
              'task.miscTaskQCVerifiedDate',
              'task.miscTaskQCVerifiedComment',
              'task.currentViewType',
              'task.isAssignable',
              ...flatMap(range(3), index => [
                `task.views[${index}].type`,
                `task.views[${index}].status`,
                `task.views[${index}].resolvedBy.id`,
                `task.views[${index}].resolvedBy.name`,
                `task.views[${index}].resolvedDate`,
              ]),
            ]}
          />
          <HiddenCheckboxes
            names={['task.isAssigned', 'task.isDone', 'task.isAwaiting']}
          />
        </form>
      </FormProvider>

      <MarkMiscTaskAsVerifiedModal
        isModalOpen={isMarkMiscTaskAsVerifiedModalOpen}
        isQCVerified={isQCVerified}
        comment={
          isQCVerified ? miscTaskQCVerifiedComment : miscTaskVerifiedComment
        }
        onSubmit={handleMarkMiscTaskAsVerified}
        closeModal={() => setIsMarkMiscTaskAsVerifiedModalOpen(false)}
      />
    </>
  )
}

BrandDefinitionTaskForm.propTypes = {
  task: PropTypes.object.isRequired,
  isSavingTask: PropTypes.bool.isRequired,
  isResolvingTask: PropTypes.bool.isRequired,
  isRefetchingTask: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
}

export default BrandDefinitionTaskForm
